import { SyntheticEvent, useEffect, useState } from 'react'
import { useLocation, useMatch, useParams, useResolvedPath } from 'react-router'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import Wrapper from '../common/Wrapper'

import { PRODUCTS as products } from 'common/constants'
import { endOfDayUTC } from 'common/api/v1/helpers'
import { useBillingApi } from '../../api/billing/billing-context'
import { ValidatedInput } from '../common/InputWithValidation'
import { findLicenseTypeById } from 'common-billing-server/index'
import {
    CreditType,
    License,
    LICENSE_TYPES as licenseTypes,
    LicenseType,
    NewLicense,
    Role,
} from 'common-billing-server/types'
import { makeEditCustomerPath, Path } from '../../routes/path'
import { notEmpty } from '../../validators'
import { commonTextFieldProps } from '../../theme/common-styles'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import SaveIcon from '@mui/icons-material/Save'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'
import { AutoComplete } from '../common/AutoComplete'
import { SearchParamKey } from '../common/Table'
import { LicenseInstallations } from './LicenseInstallations'
import { LicenseEntitlements, Product } from 'common/api/v1/types'
import { listResult } from '../../list-result'
import DataSet from 'common-ui/components/DataSet'
import { RoleBasedComponent, RoleBasedLink } from '../common/RoleBasedLink'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import type { ApiOperation } from '../common/TableActions'
import { makeDeleteLicenseOperation, makeReEnableLicenseOperation, makeRevokeLicenseOperation } from './LicenseActions'
import { formatLicenseStatus, getLicenseStatus } from './licenseStatus'

export const CreateEditLicensePage = () => {
    const billingApi = useBillingApi()
    const location = useLocation()
    const { navigateBack, navigate } = useStatefulNavigate()
    const urlParams = useParams()
    const licenseId: string | undefined = urlParams.licenseId
    const resolvedPath = useResolvedPath(Path.editLicense)
    const isEditingLicense = !!useMatch({ path: resolvedPath.pathname, end: true }) && !!licenseId

    const [customer, setCustomer] = useState<License['customer'] | null>(null)
    const [purpose, setPurpose] = useState('')
    const [domain, setDomain] = useState('')
    const [product, setProduct] = useState<Product>(products.nimbraEdge)
    const [maxTokens, setMaxTokens] = useState<number>(100)
    const [expiresAt, setExpiresAt] = useState('')
    const [framerateConversionLicensed, setFramerateConversionLicensed] = useState(false)
    const [licenseType, setLicenseType] = useState<LicenseType>(licenseTypes.usage)
    const [creditType, setCreditType] = useState(CreditType.egress)
    const [licenseKey, setLicenseKey] = useState('')
    const [license, setLicense] = useState<License>()
    const showConfirmation = useConfirmationDialog()
    const [isLoading, setIsLoading] = useState(false)
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false)
    const [validationErrors, setValidationErrors] = useState({
        customer: 'Cannot be empty' as string | undefined,
        purpose: undefined as string | undefined,
        domain: undefined as string | undefined,
    })
    const updateValidationError = (key: keyof typeof validationErrors, value: string | undefined) => {
        setValidationErrors((prevState) => ({ ...prevState, [key]: value }))
    }
    const formContainsInvalidInput = Object.values(validationErrors).some((error) => error)

    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        const operationId = 'fetch-license'

        async function fetchLicense(id: string) {
            removeFailedOperation(operationId)
            setIsLoading(true)
            try {
                const response = await billingApi.listLicenses({ filter: { licenseIds: [id] } })
                const license = response.items[0]
                if (!license) {
                    throw new Error(`No license found with id '${id}'`)
                }
                setCustomer(license.customer)
                setProduct(findProduct(license.product))
                setLicenseType(findLicenseTypeById(license.licenseType)!)
                setCreditType(license.creditType)
                updateValidationError('customer', undefined)
                setLicenseKey(license.licenseKey)
                setPurpose(license.purpose)
                setDomain(license.domain || '')
                const entitlements = license.entitlements
                if (entitlements?.product === products.connectIt.id) {
                    setMaxTokens(entitlements.maxTokens)
                }
                setExpiresAt(entitlements?.expiresAt?.toISOString().substring(0, 10) || '')
                setFramerateConversionLicensed(
                    entitlements?.product === products.nimbraEdge.id && !!entitlements.framerateConversionLicensed
                )

                setLicense(license)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching license',
                    error,
                })
            }
            setIsLoading(false)
        }

        if (isEditingLicense) {
            void fetchLicense(licenseId)
        }
    }, [licenseId, isEditingLicense])

    const createOrUpdateLicense = async (event: SyntheticEvent) => {
        event.preventDefault()

        const operationId = 'update-license'
        removeFailedOperation(operationId)
        setHasSubmittedForm(true)
        if (formContainsInvalidInput) {
            return
        }
        try {
            setIsLoading(true)

            const dateToExpiresAt = (dateString: string): Date | undefined => {
                const date = new Date(dateString)
                return isNaN(date.valueOf()) ? undefined : endOfDayUTC(date)
            }

            const expiration = dateToExpiresAt(expiresAt)
            const entitlements: LicenseEntitlements =
                product.id === products.connectIt.id
                    ? { product: products.connectIt.id, maxTokens, expiresAt: expiration }
                    : { product: products.nimbraEdge.id, framerateConversionLicensed, expiresAt: expiration }

            if (isEditingLicense) {
                await billingApi.setLicense(licenseId!, { entitlements })
                navigateBack({ replace: true })
            } else {
                const license: NewLicense = {
                    customer: customer!.id,
                    purpose,
                    domain,
                    product: product.id,
                    licenseType: licenseType.id,
                    creditType,
                    entitlements,
                }
                const createdLicense = await billingApi.createLicense(license)
                navigate(
                    {
                        pathname: Path.licenses,
                        search: `${SearchParamKey.searchString}=${createdLicense.licenseKey}`,
                    },
                    { replace: true }
                )
            }
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            addFailedOperation({
                id: operationId,
                message: `Failed ${isEditingLicense ? 'updating' : 'creating'} license`,
                error,
            })
        }
    }

    const showMaxTokensField = product.id === products.connectIt.id
    const showFramerateConversionField = product.id === products.nimbraEdge.id

    const confirmLicenseOperation = (operation: ApiOperation, deletesLicense = false) => {
        if (!license) return
        showConfirmation(
            () => {
                void (async () => {
                    setIsLoading(true)
                    removeFailedOperation(operation.id)
                    try {
                        await operation.exec(billingApi)
                        if (deletesLicense) {
                            navigate(`${Path.licenses}?${new URLSearchParams({ customerId: license.customer.id })}`, {
                                replace: true,
                            })
                        } else {
                            const { items } = await billingApi.listLicenses({ filter: { licenseIds: [license.id] } })
                            if (!items[0]) throw new Error('Failed fetching updated license')
                            setLicense(items[0])
                        }
                    } catch (error) {
                        addFailedOperation({ id: operation.id, message: `${operation.name} failed`, error })
                    } finally {
                        setIsLoading(false)
                    }
                })()
            },
            deletesLicense
                ? `Permanently delete license ${license.licenseKey}? This cannot be undone.`
                : `${operation.name} ${license.licenseKey}?`,
            {
                ok: {
                    text: deletesLicense ? 'Delete' : license.invalidatedAt ? 'Re-enable' : 'Revoke',
                    startIcon: deletesLicense ? (
                        <DeleteIcon />
                    ) : license.invalidatedAt ? (
                        <PlayCircleIcon />
                    ) : (
                        <PauseCircleIcon />
                    ),
                },
            }
        )
    }

    return (
        <Wrapper
            title={isEditingLicense ? licenseKey : undefined}
            actions={
                isEditingLicense && license?.id === licenseId ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <RoleBasedComponent minimumAllowedRole={Role.licenseAdmin}>
                            <Button
                                variant="outlined"
                                disabled={isLoading}
                                startIcon={license.invalidatedAt ? <PlayCircleIcon /> : <PauseCircleIcon />}
                                onClick={() =>
                                    confirmLicenseOperation(
                                        license.invalidatedAt
                                            ? makeReEnableLicenseOperation(license.id)
                                            : makeRevokeLicenseOperation(license.id)
                                    )
                                }
                            >
                                {license.invalidatedAt ? 'Re-enable license' : 'Revoke license'}
                            </Button>
                        </RoleBasedComponent>
                        <RoleBasedComponent minimumAllowedRole={Role.super}>
                            <Tooltip
                                title={
                                    license.activatedAt
                                        ? 'Activated licenses cannot be deleted; revoke them instead.'
                                        : ''
                                }
                            >
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disabled={isLoading || !!license.activatedAt}
                                        startIcon={<DeleteIcon />}
                                        onClick={() =>
                                            confirmLicenseOperation(makeDeleteLicenseOperation(license.id), true)
                                        }
                                    >
                                        Delete license
                                    </Button>
                                </span>
                            </Tooltip>
                        </RoleBasedComponent>
                    </Box>
                ) : undefined
            }
        >
            <Paper>
                {isEditingLicense ? (
                    <Box
                        component="form"
                        onSubmit={(e) => void createOrUpdateLicense(e)}
                        sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        <DataSet
                            values={{
                                Customer: customer ? (
                                    <RoleBasedLink
                                        minimumAllowedRole={Role.admin}
                                        to={makeEditCustomerPath(customer.id)}
                                        state={{ from: location }}
                                    >
                                        {customer.name}
                                    </RoleBasedLink>
                                ) : undefined,
                                'License key': licenseKey,
                                Status: describeLicenseStatus(license),
                                Purpose: purpose,
                                Domain: domain || '—',
                                Product: product.name,
                                'License type': licenseType.name,
                                'Contracted stream credits': creditType,
                            }}
                        />
                        {showMaxTokensField && (
                            <TextField
                                {...commonTextFieldProps()}
                                type="number"
                                label={'Max tokens'}
                                disabled={isLoading}
                                value={maxTokens}
                                onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                            />
                        )}
                        <TextField
                            {...commonTextFieldProps()}
                            type="date"
                            label={'Expiration date'}
                            disabled={isLoading}
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                        />
                        {showFramerateConversionField && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={framerateConversionLicensed}
                                        disabled={isLoading}
                                        onChange={(e) => setFramerateConversionLicensed(e.target.checked)}
                                        data-testid="framerate-conversion-licensed"
                                    />
                                }
                                label="Frame-rate conversion licensed (full quality, no demo watermark)"
                            />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            >
                                Save
                            </Button>
                        </Box>
                        {errorBanner}
                    </Box>
                ) : (
                    <form
                        onSubmit={(e) => void createOrUpdateLicense(e)}
                        style={{
                            padding: '20px',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <AutoComplete<License['customer']>
                            placeholder={`Customer`}
                            value={customer}
                            isRequired={true}
                            onValueSelected={(selected) => {
                                if (!selected) {
                                    updateValidationError('customer', 'Cannot be empty.')
                                } else if (validationErrors.customer) {
                                    updateValidationError('customer', undefined)
                                }
                                setCustomer(selected)
                            }}
                            formatSelectedValue={(customer) => customer.name}
                            error={hasSubmittedForm ? validationErrors.customer : undefined}
                            isClearable={false}
                            api={(query) =>
                                billingApi.listCustomers({ ...query, filter: { searchName: query.filter } })
                            }
                            isDisabled={isLoading || isEditingLicense}
                            dataTestId={'license-customer'}
                        />

                        <ValidatedInput
                            {...commonTextFieldProps()}
                            type="text"
                            label={'Purpose'}
                            disabled={isLoading || isEditingLicense}
                            validators={[notEmpty]}
                            value={purpose}
                            onChange={setPurpose}
                            onValidationError={(error) => updateValidationError('purpose', error)}
                            showErrorDespiteUntouched={hasSubmittedForm}
                        />

                        <ValidatedInput
                            {...commonTextFieldProps()}
                            type="text"
                            label={'Domain'}
                            placeholder={'edge.example.com'}
                            disabled={isLoading || isEditingLicense}
                            validators={[]}
                            value={domain}
                            onChange={setDomain}
                            onValidationError={(error) => updateValidationError('domain', error)}
                            showErrorDespiteUntouched={hasSubmittedForm}
                            helperText={
                                product === products.connectIt &&
                                !(domain.endsWith('.cnctit.io') || domain.endsWith('.nimbra.dev'))
                                    ? 'Only .cnctit.io sub-domains will be automatically managed'
                                    : ''
                            }
                        />

                        <AutoComplete<Product>
                            placeholder={`Product`}
                            value={product}
                            isRequired={true}
                            onValueSelected={(selected) => {
                                setProduct(selected!)
                            }}
                            formatSelectedValue={(product) => product.name}
                            error={hasSubmittedForm ? validationErrors.customer : undefined}
                            isClearable={false}
                            api={(query) => Promise.resolve(listResult([products.connectIt, products.nimbraEdge]))}
                            isDisabled={isLoading || isEditingLicense}
                            dataTestId={'product-customer'}
                        />

                        <AutoComplete<LicenseType>
                            placeholder={`License type`}
                            value={licenseType}
                            isRequired={true}
                            onValueSelected={(selected) => {
                                setLicenseType(selected!)
                            }}
                            formatSelectedValue={(product) => product.name}
                            error={hasSubmittedForm ? validationErrors.customer : undefined}
                            isClearable={false}
                            api={(query) => Promise.resolve(listResult(Object.values(licenseTypes)))}
                            isDisabled={true}
                            dataTestId={'license-type-customer'}
                        />

                        <AutoComplete<{ id: CreditType; name: string }>
                            isRequired={true}
                            placeholder={`Contracted stream credits`}
                            value={{ id: creditType, name: creditType }}
                            onValueSelected={(selected) => setCreditType(selected?.id ?? CreditType.total)}
                            formatSelectedValue={(selected) => selected.name}
                            isClearable={false}
                            isDisabled={true}
                            api={() =>
                                Promise.resolve(listResult(Object.values(CreditType).map((v) => ({ id: v, name: v }))))
                            }
                            dataTestId={'charged-credit-type'}
                        />

                        {showMaxTokensField && (
                            <TextField
                                {...commonTextFieldProps()}
                                type="number"
                                label={'Max tokens'}
                                disabled={isLoading || isEditingLicense}
                                value={maxTokens}
                                onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                            />
                        )}

                        <TextField
                            {...commonTextFieldProps()}
                            type="date"
                            label={'Expiration date'}
                            disabled={isLoading || isEditingLicense}
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                        />

                        {showFramerateConversionField && (
                            <FormControlLabel
                                sx={{ width: '100%' }}
                                control={
                                    <Checkbox
                                        checked={framerateConversionLicensed}
                                        disabled={isLoading}
                                        onChange={(e) => setFramerateConversionLicensed(e.target.checked)}
                                        data-testid="framerate-conversion-licensed"
                                    />
                                }
                                label="Frame-rate conversion licensed (full quality, no demo watermark)"
                            />
                        )}

                        {!isEditingLicense && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={formContainsInvalidInput || isLoading}
                                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                >
                                    Create License
                                </Button>
                            </Box>
                        )}

                        {errorBanner}
                    </form>
                )}
            </Paper>
            {licenseKey && (
                <>
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Installations using this license
                    </Typography>
                    <Paper>
                        <LicenseInstallations licenseKey={licenseKey} />
                    </Paper>
                </>
            )}
        </Wrapper>
    )
}

function findProduct(productId: string): Product {
    return Object.values(products).find((product) => product.id === productId)!
}

/** The detail page shows the bare invalidation reason; the license list carries the instant. */
function describeLicenseStatus(license: License | undefined): string {
    if (!license) return 'valid'
    const status = getLicenseStatus(license)
    return status.state === 'invalidated' ? status.reason : formatLicenseStatus(status)
}
